import { motion } from "framer-motion";
import { MapPin, Phone, Mail } from "lucide-react";
import {
  Box,
  IconButton,
  useTheme,
} from '@mui/material';
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Facebook, Instagram, LinkedIn } from "@mui/icons-material";
import HBN from "../../../assets/HBN.svg";
import { setFilters } from "@/redux/vendor.search.slice";
import { vendorType } from "@/constants/vendorType";

const quickLinks = [
  { name: "Home", href: "#home" },
  { name: "Find Professionals", href: "#find-professionals" },
  { name: "Categories", href: "#categories" },
  { name: "How It Works", href: "#how-it-works" },
  { name: "About Us", href: "/about-us" },
  { name: "Join as Professional", href: "#join" },
  { name: "Pricing", href: "#" },
];

const socialLinks = [
  { name: "Facebook", href: "#", icon: "fab fa-facebook-f" },
  { name: "Twitter", href: "#", icon: "fab fa-twitter" },
  { name: "LinkedIn", href: "#", icon: "fab fa-linkedin-in" },
  { name: "Instagram", href: "#", icon: "fab fa-instagram" },
];

export function FooterSection() {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const scrollToSection = (sectionId) => {
    console.log("sectionId", sectionId);
    if (sectionId == "#find-professionals") {
      navigate("/customer/search-brokers");
    } else {
      if (sectionId.startsWith("#")) {
        const element = document.getElementById(sectionId.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }
  };

  const handleCardClick = (vendor) => {
    console.log(" handleCardClick vendor", vendor);
    dispatch(
      setFilters({
        vendorType: vendor ? [vendor] : [],
        rating: null,
        serviceType: [],
      })
    );
    navigate(`/customer/search-brokers`);
  };

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="md:col-span-2"
          >
            {/* <h3 className="text-2xl font-bold mb-4">HBN-Tech</h3> */}
            <img src={HBN} alt="HBN Logo" className="h-10 w-auto" />
            <p className="text-gray-300 mb-6 max-w-md">
              Connecting you with top real estate professionals to help you achieve your property goals.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                  aria-label={social.name}
                >
                  <i className={`${social.icon} text-xl`}></i>
                </a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  {link.href.startsWith("#") ? (
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="text-gray-300 hover:text-white transition-colors duration-200 text-left"
                    >
                      {link.name}
                    </button>
                  ) : (
                    <a
                      href={link.href}
                      className="text-gray-300 hover:text-white transition-colors duration-200 block"
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Professional Services */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <h4 className="text-lg font-semibold mb-4">Professional Services</h4>
            <ul className="space-y-2">
              {vendorType.map((service) => (
                <li key={service}>
                  <button
                    onClick={() => handleCardClick(service)}
                    className="text-gray-300 hover:text-white transition-colors duration-200 w-full text-left"
                  >
                    {service}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Contact Information */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-12 pt-8 border-t border-gray-800"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">

            {/* Contact Info */}
            <div className="flex items-center">
              <MapPin className="text-yellow-400 h-6 w-6 mr-3" />
              <span className="text-gray-300">Mango Hill, Brisbane Qld, Australia</span>
            </div>
            <div className="flex items-center">
              <Phone className="text-yellow-400 h-6 w-6 mr-3" />
              <span className="text-gray-300">0406 364 893</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Mail className="text-yellow-400 h-6 w-6 mr-3" />
                <span className="text-gray-300">hello@hbn-tech.com</span>
              </div>

              {/* Social Icons */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton
                  href="https://www.facebook.com/share/1ByW3GCQJu/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  sx={{
                    color: theme.palette.primary.main,
                    "&:hover": { color: theme.palette.primary.dark },
                  }}
                >
                  <Facebook />
                </IconButton>

                <IconButton
                  href="https://www.instagram.com/hbn_tech_au/profilecard/?igsh=MWNlM3g3bWsyN3lreQ=="
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  sx={{
                    color: theme.palette.secondary.main,
                    "&:hover": { color: theme.palette.secondary.dark },
                  }}
                >
                  <Instagram />
                </IconButton>

                <IconButton
                  href="https://www.linkedin.com/company/hbn-tech/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  sx={{
                    color: theme.palette.info.main,
                    "&:hover": { color: theme.palette.info.dark },
                  }}
                >
                  <LinkedIn />
                </IconButton>
              </Box>
            </div>
          </div>
        </motion.div>

        {/* Copyright */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
          className="mt-8 pt-8 border-t border-gray-800 text-center"
        >
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400">© 2025 HBN-tech. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link
                to="/privacy-policy"
                className="text-gray-400 hover:text-white transition-colors duration-200"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-gray-400 hover:text-white transition-colors duration-200"
              >
                Terms of Service
              </Link>
              {/* <Link
                className="text-gray-400 hover:text-white transition-colors duration-200"
              >
                Cookie Policy
              </Link> */}
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}
